import React from 'react'
// Components
import BillingInfoTable from '../BillingInfoTable'
import Panel from '../Panel'

// Local Components
import { CurrentPlan } from '../../components/CurrentPlan'
import ProjectedTotal from '../ProjectedTotal/ProjectedTotal'
import BillingCycle from '../../components/BillingCycle'
import { PriceIncreaseModal } from './PriceIncreaseModal'
// GQL
import {
  BillingPlanFragment,
  BillingSeatsInfoFragment,
} from '@sketch/gql-types'

import { Banner, useModalContext } from '@sketch/components'

import { useFlag } from '@sketch/modules-common'

// Styles
import {
  CanceledPill,
  BlockText,
  StyledPanelTitle,
  LearnMoreText,
} from './PlanOverviewPanel.styles'
import BillingFor from '../BillingFor'

interface PlanOverviewPanelProps {
  workspaceId: string
  subscriptionEnd?: string
  currentPlan?: BillingPlanFragment
  seats: BillingSeatsInfoFragment
  nextBillingCycleDate?: string
  projectedCost?: number
  isWorkspaceSubscriptionActive: boolean
  isWorkspaceOnTrial: boolean
  customerHasPaymentDetails: boolean
  trialEndDate: string

  onReactivateSubscription: () => void
  onCancelSeatScheduleChange: () => void
  onSeatScheduleChange: () => void
}

/**
 * PlanOverviewPanel
 *
 * This component holds all the data logic needed to fulfill {@link PlanOverviewPanel}.
 */
export const PlanOverviewPanel: React.FC<PlanOverviewPanelProps> = props => {
  const {
    workspaceId,
    isWorkspaceSubscriptionActive,
    isWorkspaceOnTrial,
    customerHasPaymentDetails,
    currentPlan,
    subscriptionEnd,
    seats,
    nextBillingCycleDate,
    projectedCost,
    trialEndDate,
    onReactivateSubscription,
    onCancelSeatScheduleChange,
    onSeatScheduleChange,
  } = props
  const { showModal } = useModalContext()
  const isNewPricingFF = useFlag('new_pricing')

  const isNewPricingEnabled =
    isNewPricingFF && currentPlan?.product !== 'BUSINESS'
  const isSubscriptionEnd = !!subscriptionEnd

  if (!isWorkspaceSubscriptionActive) {
    return (
      <Panel>
        <StyledPanelTitle>Plan Overview</StyledPanelTitle>
        <Panel.Body>
          <BillingInfoTable.Row>
            <BillingInfoTable.HeadingCell>
              Current Plan
            </BillingInfoTable.HeadingCell>
            <BillingInfoTable.Cell>
              <div>
                <CanceledPill data-testid="canceled-subscription-badge">
                  Subscription Canceled
                </CanceledPill>
                <BlockText>
                  Your Workspace subscription has been canceled and documents
                  are now in read-only mode.
                </BlockText>
              </div>
            </BillingInfoTable.Cell>
          </BillingInfoTable.Row>
        </Panel.Body>
      </Panel>
    )
  }

  const onLearnMore = () => {
    showModal(PriceIncreaseModal)
  }

  return (
    <Panel>
      {isNewPricingEnabled && (
        <Banner type="information">
          From <strong>19 May 2023</strong> onwards subscription prices will
          increase from <strong>$9 to $12</strong> per Editor for monthly plans,
          and <strong>$99 to $120</strong> per Editor for yearly plans.{' '}
          <LearnMoreText onClick={onLearnMore}>Learn More</LearnMoreText>
        </Banner>
      )}
      <StyledPanelTitle>Plan Overview</StyledPanelTitle>
      <Panel.Body>
        <BillingInfoTable>
          <CurrentPlan
            workspaceId={workspaceId}
            isWorkspaceOnTrial={isWorkspaceOnTrial}
            customerHasPaymentDetails={customerHasPaymentDetails}
            currentPlan={currentPlan}
            trialEndDate={trialEndDate}
            isSubscriptionEnd={isSubscriptionEnd}
            nextBillingCycleDate={nextBillingCycleDate}
            isNewPricingEnabled={isNewPricingEnabled}
            onReactivateSubscription={onReactivateSubscription}
          />
          <BillingFor
            seats={seats}
            isSubscriptionEnd={isSubscriptionEnd}
            onSeatScheduleChange={onSeatScheduleChange}
          />
          <ProjectedTotal
            projectedCost={projectedCost}
            seats={seats}
            hasSubscription={customerHasPaymentDetails}
          />
          <BillingCycle
            nextBillingCycleDate={nextBillingCycleDate}
            subscriptionEnd={subscriptionEnd}
            seats={seats}
            isWorkspaceOnTrial={isWorkspaceOnTrial}
            customerHasPaymentDetails={customerHasPaymentDetails}
            onCancelSeatScheduleChange={onCancelSeatScheduleChange}
            currentPlan={currentPlan}
          />
        </BillingInfoTable>
      </Panel.Body>
    </Panel>
  )
}
